import {
  DataTable,
  DataTableColDef,
  DataTableColumnHeader,
} from "@/lib/Components/DataTable/DataTable";
import {
  SupplierOfficeListItem,
  supplierOfficeListQuery,
} from "@/app/Offices/GraphQL/supplierOfficeListQuery";
import type { TableId } from "@/app/Common/Utils/table-id";

import { useOfficeActions } from "@/app/Offices/Hooks/useOfficeActions";
import { useGlobalSupplier } from "@/app/Suppliers/Utils/useGlobalSupplier";
import {
  OrderByRelationWithColumnAggregateFunction,
  QuerySupplierOfficesOrderByCityColumn,
  QuerySupplierOfficesOrderByColumn,
  SortOrder,
  SupplierOfficeListQueryVariables,
} from "@/gql/graphql";
import { ReactNode } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useTranslations } from "use-intl";

export type OfficeTableId = "name" | "actions" | "city" | "code" | "address";

export function OfficeTable({
  id,
  columns,
  queryVariables,
  rightButtons,
  title,
}: {
  id: TableId;
  columns?: DataTableColDef<SupplierOfficeListItem, any, OfficeTableId>[];
  queryVariables?: Partial<SupplierOfficeListQueryVariables>;
  rightButtons?: ReactNode;
  title?: string;
}) {
  const t = useTranslations("office.table");
  const { supplierId } = useGlobalSupplier();
  const getActions = useOfficeActions();
  const defaultColumns: DataTableColDef<
    SupplierOfficeListItem,
    any,
    OfficeTableId
  >[] = [
    {
      id: "name",
      accessorFn: (data) => data.name,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("name")} />
      ),
      cell: ({ row }) => {
        return <OfficeNameCell office={row.original} />;
      },
    },
    {
      id: "code",
      isInitiallyHidden: true,
      accessorFn: (data) => data.code ?? "--",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("code")} />
      ),
    },
    {
      id: "city",
      isInitiallyHidden: true,
      accessorFn: (data) => data.city.name,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("city")} />
      ),
    },
    {
      id: "address",
      header: t("address"),
      cell: ({ row: { original } }) => (
        <div className="text-xs text-gray-500">
          <p>{original.address.formatted_address_line_1 || "--"}</p>
          <p>{original.address.formatted_address_line_2}</p>
        </div>
      ),
    },
  ];

  return (
    <DataTable
      title={title}
      getActions={getActions}
      columns={columns ?? defaultColumns}
      document={supplierOfficeListQuery}
      getQueryVariables={({ pagination, sorting, search }) => {
        const baseParams: SupplierOfficeListQueryVariables = {
          search,
          first: pagination.pageSize,
          page: pagination.pageIndex,
          supplier_id: supplierId,
          ...queryVariables,
        };

        if (baseParams.search) {
          return baseParams;
        }

        return {
          ...baseParams,
          orderBy: sorting
            .map((sort) => {
              const order = sort.desc ? SortOrder.Desc : SortOrder.Asc;
              if (sort.id === "name") {
                return {
                  column: QuerySupplierOfficesOrderByColumn.Name,
                  order,
                };
              }

              if (sort.id === "code") {
                return {
                  column: QuerySupplierOfficesOrderByColumn.Code,
                  order,
                };
              }

              if (sort.id === "city") {
                return {
                  city: {
                    column: QuerySupplierOfficesOrderByCityColumn.Name,
                    aggregate: OrderByRelationWithColumnAggregateFunction.Min,
                  },
                  order,
                };
              }

              console.warn(`No sorting for ${sort.id}`);
              return null;
            })
            .filter(Boolean),
        };
      }}
      accessor={(data) => data.supplierOffices}
      id={id}
      initialSorting={[{ id: "name", desc: false }]}
      rightButtons={rightButtons}
    />
  );
}

function OfficeNameCell({ office }: { office: SupplierOfficeListItem }) {
  const navigate = useNavigate();

  function handleClick() {
    return navigate({
      to: ".",
      search: (prev: any) => ({
        ...prev,
        officeId: office.id,
      }),
    });
  }

  return (
    <button
      onClick={handleClick}
      className="text-left font-medium text-indigo-600 hover:text-indigo-900"
    >
      {office.name}
    </button>
  );
}
